import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { cartApi } from '../api/cart';
import { getAccessToken, hadSession, setSessionExpiredHandler } from '../api/client';
import type { CurrentUserDto, LoginDto, LoginResultDto } from '../api/types';

interface AuthState {
  user: CurrentUserDto | null;
  /** Ilk acilista oturum geri yukleniyor. */
  booting: boolean;
  isAuthenticated: boolean;
  login: (dto: LoginDto) => Promise<LoginResultDto>;
  completeTwoFactor: (result: LoginResultDto) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [booting, setBooting] = useState(true);
  const queryClient = useQueryClient();

  const loadUser = useCallback(async () => {
    try {
      setUser(await authApi.me());
    } catch {
      setUser(null);
    }
  }, []);

  /**
   * Acilista oturumu geri yukler. Access token sayfa yenilemesinde
   * sessionStorage'dan gelir; yoksa HttpOnly refresh cerezi ile yenilenir.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Daha once giris yapilmadiysa yenileme denemesi bosunadir.
        if (!getAccessToken() && hadSession()) {
          await authApi.refresh();
        }
        if (getAccessToken() && !cancelled) {
          await loadUser();
        }
      } catch {
        /* Misafir olarak devam edilir. */
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  /** Yenileme de basarisiz olduysa arayuz misafire doner. */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setSessionExpiredHandler(null);
  }, [queryClient]);

  /**
   * Giris sonrasi misafir sepeti uye sepetiyle birlestirilir; giris
   * oncesi eklenen urunler kaybolmaz.
   */
  const afterAuthenticated = useCallback(async () => {
    await loadUser();
    try {
      await cartApi.merge();
    } catch {
      /* Birlestirme basarisiz olsa da giris gecerlidir. */
    }
    await queryClient.invalidateQueries();
  }, [loadUser, queryClient]);

  const login = useCallback(
    async (dto: LoginDto) => {
      const result = await authApi.login(dto);

      // Iki adimli dogrulama acikken token gelmez; cagiran taraf kod ekranini acar.
      if (!result.requiresTwoFactor) {
        await afterAuthenticated();
      }

      return result;
    },
    [afterAuthenticated],
  );

  const completeTwoFactor = useCallback(async () => {
    await afterAuthenticated();
  }, [afterAuthenticated]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      booting,
      isAuthenticated: user !== null,
      login,
      completeTwoFactor,
      logout,
      refresh: loadUser,
    }),
    [user, booting, login, completeTwoFactor, logout, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
